// ============================================================================
// RemoteWebRTCDeviceV3.tsx — device distant, flux audio/vidéo vers la régie
//
// Reprend V2 en ajoutant la connexion par lien : quand l'URL est de la forme
// /webrtc/{uuidChaine}{token}, les 16 premiers caractères désignent la chaîne
// (donc le microservice à interroger) et les 5 suivants identifient le device.
// Si le microservice reconnaît ce token, la page s'authentifie seule ; sinon —
// ou si l'URL ne porte que l'UUID de la chaîne — on retombe sur la page de
// connexion identifiant / mot de passe historique.
// ============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { parseDeviceAccessUuid, buildDeviceAccessUuid, DEVICE_ACCESS_TOKEN_LENGTH } from '../../../dataTypes/liveStreamManagment/utils_deviceAccess';
import { resolveDeviceAccessToken } from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import { SdpLogEntry } from '../../../dataTypes/liveStreamManagment/types_sdp';
import {
  DeviceInfo, ActiveStreamDetails, GeolocationInfo,
  OrientationInfo, CodecInfo, RequiredCodec,
} from '../../../dataTypes/liveStreamManagment/types_media';

import { Icon } from '../../widget/images/Icon';

import RemoteDevice_InfoPanel from './RemoteDevice_InfoPanel';

// ── API modules ───────────────────────────────────────────────────────────────
import {
  connectDeviceToSignaling,
  closeDeviceSignaling,
  sendDirectorTake,
  sendNetworkQuality,
} from '../../../backendServices/liveStreamManagment/api_deviceSignaling';
import { DeviceSignalingConfig, DeviceSfuInfo, DirectorDeckSource } from '../../../dataTypes/liveStreamManagment/types_callbacks';
import {
  publishDeviceWhip,
  stopDeviceWhip,
  replaceDeviceWhipTracks,
  isDeviceWhipActive,
  getDeviceWhipPeerConnection,
  playProgramWhep,
  stopProgramWhep,
} from '../../../backendServices/liveStreamManagment/api_deviceWhip';
import {
  sampleNetworkQuality, applySample, initialNetworkQualityState,
  QUALITY_LABELS, describeSample,
  NetworkQualityState,
} from '../../../backendServices/liveStreamManagment/api_networkQuality';
import {
  createDeviceOffer,
  handleDeviceRemoteOffer,
  handleDeviceRemoteAnswer,
  handleDeviceRemoteIceCandidate,
  closeDevicePeerConnection,
  handleDeviceReturnOffer,
  handleDeviceReturnIceCandidate,
  closeDeviceReturnPeerConnection,
  replaceDeviceTrack,
} from '../../../backendServices/liveStreamManagment/api_deviceWebRTC';

import { DeviceWebRTCRefs, DeviceWebRTCCallbacks } from '../../../dataTypes/liveStreamManagment/types_callbacks';

import {
  enumerateDeviceMediaDevices,
  captureDeviceStream,
  changeDeviceCamera,
  changeDeviceMic,
  collectActiveStreamDetails,
  releaseDeviceStream,
} from '../../../backendServices/liveStreamManagment/api_deviceMedia';

import { MediaDeviceEntry } from '../../../dataTypes/liveStreamManagment/types_media';

import { Mic, MicOff, Volume2, VolumeX, Eye, EyeOff, Camera } from 'lucide-react'

import {
  handleDeviceIntercommReceiveOffer,
  handleDeviceIntercommSendOffer,
  handleDeviceIntercommReceiveIce,
  handleDeviceIntercommSendIce,
  closeDeviceIntercomm,
  DeviceIntercommRefs,
} from '../../../backendServices/liveStreamManagment/api_deviceIntercomm';


// ── Logger distant ────────────────────────────────────────────────────────────
import { createLogger } from '../../../backendServices/debugManagment/remoteLogger';
const logger = createLogger('RemoteWebRTCDeviceV3');

// ─── Helpers plateforme ───────────────────────────────────────────────────────

const isIOS     = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isiPad    = /iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = /Android/.test(navigator.userAgent);

const getPlatformInfo = (): DeviceInfo => {
  const ua  = navigator.userAgent;
  const platform = isIOS ? 'iOS' : isiPad ? 'iPadOS' : isAndroid ? 'Android' : 'Desktop';
  let version = 'Unknown';
  if (isIOS || isiPad) { const m = ua.match(/OS (\d+)_(\d+)/);       if (m) version = `${m[1]}.${m[2]}`; }
  else if (isAndroid)  { const m = ua.match(/Android (\d+(\.\d+)?)/); if (m) version = m[1]; }
  let browser = 'Unknown';
  if      (/CriOS/.test(ua))                        browser = 'Chrome iOS';
  else if (/FxiOS/.test(ua))                        browser = 'Firefox iOS';
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
  else if (/Chrome/.test(ua))                       browser = 'Chrome';
  else if (/Firefox/.test(ua))                      browser = 'Firefox';
  const nav  = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  return {
    platform, version, userAgent: ua, browser,
    memory:              nav.deviceMemory,
    hardwareConcurrency: navigator.hardwareConcurrency,
    connection:          conn?.effectiveType,
    connectionDownlink:  conn?.downlink,
    connectionRtt:       conn?.rtt,
  };
};

// Gain du retour régie pendant l'intercomm (ducking anti-larsen)
const INTERCOMM_DUCKING_GAIN = 0.05;

// ── Graduations dB du potentiomètre (affichage régulier) ──
const DB_MARKS = [6, 0, -6, -12, -18, -24, -36, -48, -60];
// Position (0-100, "bottom") du curseur alignée sur la graduation 0 dB au lancement
const DB_ZERO_INDEX = DB_MARKS.indexOf(0);
const DEFAULT_VOLUME_POSITION = ((DB_MARKS.length - 1 - DB_ZERO_INDEX) / (DB_MARKS.length - 1)) * 100;

// ── Dimensions du potentiomètre — la poignée déborde légèrement de part et d'autre du guide ──
const POTENTIOMETER_TRACK_W    = 12;
const POTENTIOMETER_HANDLE_OVF = 6;
const POTENTIOMETER_HANDLE_H   = 8;
// Colonne des graduations : les valeurs sont affichées sans l'unité « dB » (redondante
// sur un vu-mètre), d'où une colonne étroite — la largeur gagnée va aux vidéos.
// 28 px = tiret 6 + gouttière 4 + 18 px de texte, ce qui tient « -60 » sans rogner.
const POTENTIOMETER_MARKS_W    = 28;
// Écart entre la vidéo et le bloc vu-mètre + bouton.
const POTENTIOMETER_COL_GAP    = 12;

// ── Accroche magnétique + code couleur autour du 0 dB ──
const POTENTIOMETER_SNAP_RANGE   = 2;   // tolérance (en %) pour l'accroche au 0 dB
const POTENTIOMETER_COLOR_ZERO   = '#94a3b8'; //'#eab308'; // jaune — exactement à 0 dB
const POTENTIOMETER_COLOR_ABOVE  = '#94a3b8'; //'#ef4444'; // rouge — au-dessus de 0 dB
const POTENTIOMETER_COLOR_BELOW  = '#94a3b8'; // gris — en-dessous de 0 dB

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const RemoteWebRTCDeviceV3 = () => {

  // ── UUID depuis l'URL (/webrtc/:uuid) ─────────────────────────────────────
  // Le paramètre porte soit l'UUID de la chaîne seul (page de login classique),
  // soit l'UUID de la chaîne suivi du token de 5 caractères du device.
  const { uuid: uuidFromUrl } = useParams<{ uuid?: string }>();
  const parsedUrlUuid = parseDeviceAccessUuid(uuidFromUrl);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [deviceId,        setDeviceId]        = useState('');
  const [password,        setPassword]        = useState('');
  const [channelUUID,     setChannelUUID]     = useState('');
  const [authError,       setAuthError]       = useState('');
  const [loading,         setLoading]         = useState(false);
  const [showPassword,    setShowPassword]    = useState(false);

  // ── Connexion par lien ────────────────────────────────────────────────────
  // resolvingAccess : le token de l'URL est en cours de vérification auprès du
  // microservice de la chaîne — on n'affiche pas encore le formulaire, pour ne
  // pas faire clignoter une page de login qui va disparaître.
  const [resolvingAccess, setResolvingAccess] = useState(!!parsedUrlUuid?.accessToken);
  const [deviceLabel,     setDeviceLabel]     = useState('');
  const accessTokenRef = useRef<string>('');
  // Après une déconnexion volontaire d'une session ouverte par lien, on ne
  // redemande pas UUID/identifiant/mot de passe — que le token.
  const [tokenFormVisible, setTokenFormVisible] = useState(false);
  const [tokenInput,       setTokenInput]       = useState('');
  // Device résolu par le microservice, en attente de l'énumération des caméras.
  const [pendingAutoLogin, setPendingAutoLogin] = useState<{ deviceId: string; channelUUID: string; accessToken: string } | null>(null);
  const autoLoginStartedRef = useRef(false);

  // ── Media ─────────────────────────────────────────────────────────────────
  const [connected,        setConnected]        = useState(false);
  // Lien de signalisation ouvert : distingue « en attente de la régie » (WS ouverte,
  // flux pas encore négocié) d'une perte de réseau (WS tombée, reconnexion en cours).
  const [signalingOpen,    setSignalingOpen]    = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceEntry[]>([]);
  const [selectedCamera,   setSelectedCamera]   = useState('');
  const [availableMics,    setAvailableMics]     = useState<MediaDeviceEntry[]>([]);
  const [selectedMic,      setSelectedMic]       = useState('');

  // ── UI ────────────────────────────────────────────────────────────────────
  const [showInfoPanel,       setShowInfoPanel]       = useState(false);
  // Popup affichée quand la régie coupe le device : visible 5 s par-dessus la page
  // de connexion, où l'on est déjà revenu (la capture est coupée immédiatement).
  const [forcedDisconnect,    setForcedDisconnect]    = useState(false);
  const forcedDisconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (forcedDisconnectTimerRef.current) clearTimeout(forcedDisconnectTimerRef.current);
  }, []);
  // Vidéo actuellement en plein écran (fallback CSS, quand la Fullscreen API est indisponible)
  const [fullscreenTargetCSS, setFullscreenTargetCSS] = useState<'retour' | 'monFlux' | null>(null);
  const [activeStreamDetails, setActiveStreamDetails] = useState<ActiveStreamDetails | null>(null);
  const [geolocation,         setGeolocation]         = useState<GeolocationInfo>({ loading: false });
  const [orientation,         setOrientation]         = useState<OrientationInfo>({ alpha: null, beta: null, gamma: null, absolute: false });
  const [codecInfo,           setCodecInfo]           = useState<CodecInfo>({
    available: { video: [], audio: [] },
    negotiatedWithRegie: null,
    lastSdpOffer: null,
    lastSdpAnswer: null,
  });

  // ── Refs ──────────────────────────────────────────────────────────────────
  const videoRef            = useRef<HTMLVideoElement>(null);
  const retourContainerRef  = useRef<HTMLDivElement>(null);
  const monFluxContainerRef = useRef<HTMLDivElement>(null);

  const wsRef                 = useRef<WebSocket | null>(null);
  // Bloc SFU reçu dans auth_success (null → mode P2P historique)
  const sfuInfoRef            = useRef<DeviceSfuInfo | null>(null);
  const pcRef                 = useRef<RTCPeerConnection | null>(null);

  // ── Qualité du réseau montant ──────────────────────────────────────────────
  // Mesurée sur la PeerConnection qui PORTE le flux caméra, quelle qu'elle soit :
  // celle de la publication WHIP en mode SFU, celle de la régie en mode P2P. Les
  // connexions du retour programme et de l'intercom sont volontairement ignorées
  // — elles descendent, alors que c'est la montée qui décide de ce que voit
  // l'antenne.
  const [networkQuality, setNetworkQuality] = useState<NetworkQualityState>(initialNetworkQualityState);
  const networkStatsRef = useRef<any>(null);
  const streamRef             = useRef<MediaStream | null>(null);
  const iceCandidateBufferRef = useRef<RTCIceCandidateInit[]>([]);
  const remoteDescSetRef      = useRef<boolean>(false);
  const requiredCodecRef      = useRef<RequiredCodec | null>(null);
  const shouldReconnectRef    = useRef<boolean>(false);

  // Refs miroir pour callbacks stables
  const isAuthenticatedRef = useRef(false);
  const deviceIdRef        = useRef('');
  const passwordRef        = useRef('');
  const channelUUIDRef     = useRef('');
  const selectedCameraRef  = useRef('');
  const selectedMicRef     = useRef('');

  // ── Potentiomètre + mute — volume du micro (flux envoyé) ─────────────────
  const [previewVolume, setPreviewVolume] = useState(DEFAULT_VOLUME_POSITION); // position 0-100, alignée sur la graduation 0 dB
  const previewVolumeRef      = useRef(1); // gain courant dérivé de previewVolume
  const [previewMuted, setPreviewMuted] = useState(false);
  const previewMutedRef       = useRef(false);
  const previewSendCtxRef      = useRef<AudioContext | null>(null);
  const previewSendGainNodeRef = useRef<GainNode | null>(null);
  const previewRawMicTrackRef  = useRef<MediaStreamTrack | null>(null);

  // ── Potentiomètre + mute — volume du retour régie ────────────────────────
  const [retourVolume, setRetourVolume] = useState(DEFAULT_VOLUME_POSITION); // position 0-100, alignée sur la graduation 0 dB
  const retourVolumeRef       = useRef(1);
  const [retourMuted, setRetourMuted] = useState(false);
  const retourMutedRef       = useRef(false);

  // ── VU-mètre — niveau affiché sur les potentiomètres ─────────────────────
  // Micro : canal gauche du flux envoyé. Retour : moyenne gauche/droite du flux reçu.
  const [micLevel,    setMicLevel]    = useState(0);
  const [retourLevel, setRetourLevel] = useState(0);
  const micMeterAnalyserRef      = useRef<AnalyserNode | null>(null);
  const micMeterRafRef           = useRef<number | null>(null);
  const retourMeterAnalyserRef   = useRef<AnalyserNode | null>(null);
  const retourMeterRafRef        = useRef<number | null>(null);

  const hlsVideoRef   = useRef<HTMLVideoElement>(null);

  const pcReturnRef      = useRef<RTCPeerConnection | null>(null);
  const returnStreamRef  = useRef<MediaStream | null>(null);
  const iceBufReturnRef  = useRef<RTCIceCandidateInit[]>([]);

  // ── Intercomm ──────────────────────────────────────────────────────────────
  const pcIntercommReceiveRef     = useRef<RTCPeerConnection | null>(null);
  const iceBufIntercommReceiveRef = useRef<RTCIceCandidateInit[]>([]);
  const intercommReceiveStreamRef = useRef<MediaStream | null>(null);
  const pcIntercommSendRef        = useRef<RTCPeerConnection | null>(null);
  const iceBufIntercommSendRef    = useRef<RTCIceCandidateInit[]>([]);
  const intercommSendStreamRef    = useRef<MediaStream | null>(null);
  const intercommAudioRef         = useRef<HTMLAudioElement | null>(null);
  const intercommSilenceCtxRef    = useRef<AudioContext | null>(null);
  const [intercommActive, setIntercommActive] = useState(false);

  // ── On air (flux final régie) ─────────────────────────────────────────────
  const [isOnAir, setIsOnAir] = useState(false);

  // ── Rôle Réalisateur (accordé par la régie) ───────────────────────────────
  // Piloté uniquement par le message WS `director` : le device ne se l'attribue
  // jamais lui-même. Hors rôle, l'onglet « Réalisateur » n'existe pas.
  const [isDirector, setIsDirector] = useState(false);
  const [activeTab,  setActiveTab]  = useState<'general' | 'director'>('general');

  // Mini-deck : reflet du pupitre régie, entièrement piloté par `director_deck`.
  // Le device ne devine ni les libellés ni la source en final — il n'affiche que
  // ce qu'on lui a envoyé, et n'a donc rien à montrer avant le premier message.
  const [directorSources, setDirectorSources] = useState<DirectorDeckSource[]>([]);
  const [directorFinalId, setDirectorFinalId] = useState<string | null>(null);

  // Pipeline Web Audio du retour — pour le contrôle de volume via GainNode
  // (.volume est read-only sur iOS/Safari, d'où ce pipeline)
  const returnAudioCtxRef  = useRef<AudioContext | null>(null);
  const returnGainNodeRef  = useRef<GainNode | null>(null);

  // ── Sélection caméra au login ─────────────────────────────────────────────
  const [loginCamerasReady, setLoginCamerasReady] = useState(false);
  const [loginCamerasError, setLoginCamerasError] = useState('');

  const SERVER_URL = 'cast.kleonpro.tv';

  // ── Synchronisation refs ──────────────────────────────────────────────────
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);
  useEffect(() => { deviceIdRef.current    = deviceId;    }, [deviceId]);
  useEffect(() => { passwordRef.current    = password;    }, [password]);
  useEffect(() => { channelUUIDRef.current = channelUUID; }, [channelUUID]);
  useEffect(() => { selectedCameraRef.current = selectedCamera; }, [selectedCamera]);
  useEffect(() => { selectedMicRef.current    = selectedMic;    }, [selectedMic]);

  // ── addSdpLog ─────────────────────────────────────────────────────────────
  // remote = true (défaut) : forwarde aussi vers le fichier distant via logger
  const addSdpLog = useCallback((
    entry  : Omit<SdpLogEntry, 'id' | 'timestamp'>,
    remote : boolean = true,
  ) => {
    // ── Log distant ───────────────────────────────────────────────────────
    if (remote) {
      // Préfixe selon la direction
      const prefixMap: Record<string, string> = {
        sent     : '[SDP SENT]',
        received : '[SDP RECEIVED]',
        info     : '[SDP INFO]',
      };
      const prefix = prefixMap[entry.direction] ?? '[SDP]';

      // Niveau : error si type=error, info sinon
      if (entry.type === 'error') {
        logger.error(`${prefix} ${entry.label}`);
      } else {
        logger.info(`${prefix} ${entry.label}`);
      }
    }
  }, []);

  // ── Refs WebRTC partagées ─────────────────────────────────────────────────
  const webRtcRefs: DeviceWebRTCRefs = {
    wsRef, pcRef, streamRef,
    iceCandidateBufferRef,
    remoteDescSetRef, requiredCodecRef, shouldReconnectRef,
  };

  const negotiatedProfileRef = useRef<string | null>(null);

  // ── Callbacks WebRTC ──────────────────────────────────────────────────────
  const webRtcCallbacks: DeviceWebRTCCallbacks = {
    addSdpLog,
    onConnected: () => {
      setConnected(true);
    },
    onDisconnected: () => {
      setConnected(false);
      setIsOnAir(false);
    },
    onNegotiatedCodecs: (codecs) => {
      negotiatedProfileRef.current = codecs.videoProfile ?? null;
      setCodecInfo(prev => ({
        ...prev,
        negotiatedWithRegie: {
          videoCodec:   codecs.videoCodec,
          videoProfile: codecs.videoProfile,
          audioCodec:   codecs.audioCodec,
          payloadType:  codecs.payloadType,
        },
      }));
    },
    onRetry: async () => { await handleCreateOffer(); },
  };


  // ============================================================================
  // Énumération des périphériques au chargement (avant login)
  // ============================================================================

useEffect(() => {
  const initDevices = async () => {
    try {
      const { cameras, mics, defaultCameraId, defaultMicId } =
        await enumerateDeviceMediaDevices({ addSdpLog });
      setAvailableCameras(cameras);
      setAvailableMics(mics);

      const frontCam = cameras.find(c => /front|avant|user|facingMode=user/i.test(c.label));
      const preferredCameraId = frontCam?.deviceId ?? defaultCameraId;

      setSelectedCamera(preferredCameraId);
      selectedCameraRef.current = preferredCameraId;
      setSelectedMic(defaultMicId);
      selectedMicRef.current = defaultMicId;
      setLoginCamerasReady(true);
    } catch (err: any) {
      const msg = "Impossible d'accéder à la caméra/au micro : " + (err?.message ?? err);
      logger.error(msg);
      setLoginCamerasError(msg);
    }
  };
  initDevices();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // ============================================================================
  // Initialisation
  // ============================================================================

  useEffect(() => {
    // Codec capabilities
    if (RTCRtpSender.getCapabilities) {
      const v = RTCRtpSender.getCapabilities('video');
      const a = RTCRtpSender.getCapabilities('audio');
      setCodecInfo(prev => ({
        ...prev,
        available: {
          video: v?.codecs.map(c => c.mimeType.split('/')[1]).filter((v, i, a) => a.indexOf(v) === i) ?? [],
          audio: a?.codecs.map(c => c.mimeType.split('/')[1]).filter((v, i, a) => a.indexOf(v) === i) ?? [],
        },
      }));
    }

    // Orientation
    const handleOrientation = (e: DeviceOrientationEvent) => setOrientation({
      alpha:    e.alpha    !== null ? Math.round(e.alpha    * 10) / 10 : null,
      beta:     e.beta     !== null ? Math.round(e.beta     * 10) / 10 : null,
      gamma:    e.gamma    !== null ? Math.round(e.gamma    * 10) / 10 : null,
      absolute: e.absolute,
    });
    window.addEventListener('deviceorientation', handleOrientation);

    // Restauration credentials depuis localStorage
    const savedId   = localStorage.getItem('device_id');
    const savedPwd  = localStorage.getItem('device_password');
    const savedUUID = localStorage.getItem('channel_uuid');
    if (savedId && savedPwd) { setDeviceId(savedId); setPassword(savedPwd); }
    if (savedUUID) setChannelUUID(savedUUID);

    // UUID depuis l'URL (:uuid param) — prioritaire sur le localStorage.
    // Seule la partie « chaîne » alimente le champ ; le token éventuel est traité
    // par l'effet de connexion par lien plus bas.
    if (parsedUrlUuid) {
      setChannelUUID(parsedUrlUuid.channelUuid);
      channelUUIDRef.current = parsedUrlUuid.channelUuid;
    }

    return () => {
      shouldReconnectRef.current = false;
      releaseDeviceStream(streamRef, videoRef.current, { addSdpLog });
      closeDeviceSignaling({ wsRef });
      closeDevicePeerConnection(webRtcRefs, { addSdpLog });
      closeDeviceReturnPeerConnection(pcReturnRef, iceBufReturnRef, { addSdpLog });
      stopMicSendGain();
      if (retourMeterRafRef.current) { cancelAnimationFrame(retourMeterRafRef.current); retourMeterRafRef.current = null; }
      retourMeterAnalyserRef.current = null;
      setRetourLevel(0);
      if (returnAudioCtxRef.current) {
        returnAudioCtxRef.current.close().catch(() => {});
        returnAudioCtxRef.current = null;
        returnGainNodeRef.current  = null;
      }
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fullscreen (par vidéo — retour ou mon flux, indépendamment) ───────────
  const toggleFullscreen = useCallback(async (target: 'retour' | 'monFlux') => {
    const el = (target === 'retour' ? retourContainerRef : monFluxContainerRef).current;
    if (!el) return;
    const nativeFsEl  = document.fullscreenElement || (document as any).webkitFullscreenElement;
    const isThisActive = fullscreenTargetCSS === target || nativeFsEl === el;
    if (!isThisActive) {
      try {
        if (el.requestFullscreen)                     await el.requestFullscreen();
        else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
        else                                          setFullscreenTargetCSS(target);
      } catch (err: any) {
        logger.warn('Fullscreen API indisponible, fallback CSS', err?.message);
        setFullscreenTargetCSS(target);
      }
    } else {
      if (document.exitFullscreen)                     await document.exitFullscreen();
      else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
      setFullscreenTargetCSS(null);
    }
  }, [fullscreenTargetCSS]);

  useEffect(() => {
    const sync  = () => { if (!(document.fullscreenElement || (document as any).webkitFullscreenElement)) setFullscreenTargetCSS(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreenTargetCSS(null); };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const nativeFullscreenEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
  const isRetourFullscreen  = fullscreenTargetCSS === 'retour'  || nativeFullscreenEl === retourContainerRef.current;
  const isMonFluxFullscreen = fullscreenTargetCSS === 'monFlux' || nativeFullscreenEl === monFluxContainerRef.current;

  // ── Potentiomètre : conversion position (0-100) → gain (0 dB au milieu) ──
  const POTENTIOMETER_GAIN_MAX = 2; // position 100 = +100% (boost)
  const positionToGain = (pos: number) => (
    pos <= 50 ? pos / 50 : 1 + ((pos - 50) / 50) * (POTENTIOMETER_GAIN_MAX - 1)
  );

  // ── Pipeline de gain sur le micro envoyé à la régie ──────────────────────
  // Insère un GainNode entre le micro capturé et la piste réellement envoyée
  // (via une MediaStreamAudioDestinationNode), pour que le potentiomètre agisse
  // sur le volume perçu par la régie, pas seulement en local.
  const attachMicSendGain = useCallback((rawStream: MediaStream): MediaStream => {
    const audioTrack = rawStream.getAudioTracks()[0];
    if (!audioTrack) return rawStream;

    if (previewSendCtxRef.current) {
      try { previewSendCtxRef.current.close(); } catch {}
      previewSendCtxRef.current = null;
    }
    if (previewRawMicTrackRef.current && previewRawMicTrackRef.current !== audioTrack) {
      previewRawMicTrackRef.current.stop();
    }
    previewRawMicTrackRef.current = audioTrack;

    const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtxClass();
    previewSendCtxRef.current = ctx;

    const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
    const gain   = ctx.createGain();
    gain.gain.value = previewMutedRef.current ? 0 : previewVolumeRef.current;
    previewSendGainNodeRef.current = gain;

    const dest = ctx.createMediaStreamDestination();
    source.connect(gain);
    gain.connect(dest);

    // VU-mètre micro (canal gauche affiché sur le potentiomètre)
    if (micMeterRafRef.current) { cancelAnimationFrame(micMeterRafRef.current); micMeterRafRef.current = null; }
    const meterAnalyser = ctx.createAnalyser();
    meterAnalyser.fftSize = 256;
    meterAnalyser.smoothingTimeConstant = 0.3;
    gain.connect(meterAnalyser);
    micMeterAnalyserRef.current = meterAnalyser;
    const meterData = new Uint8Array(meterAnalyser.frequencyBinCount);
    const meterLoop = () => {
      if (!micMeterAnalyserRef.current) return;
      micMeterAnalyserRef.current.getByteFrequencyData(meterData);
      let peak = 0;
      for (let i = 0; i < meterData.length; i++) if (meterData[i] > peak) peak = meterData[i];
      setMicLevel(Math.round((peak / 255) * 100));
      micMeterRafRef.current = requestAnimationFrame(meterLoop);
    };
    micMeterRafRef.current = requestAnimationFrame(meterLoop);

    const processedTrack = dest.stream.getAudioTracks()[0];
    rawStream.removeTrack(audioTrack);
    rawStream.addTrack(processedTrack);

    logger.info('Pipeline de gain micro (envoi) branché');
    addSdpLog({ direction: 'info', type: 'event', label: '🎚️ Pipeline de gain micro (envoi) branché' });
    return rawStream;
  }, [addSdpLog]);

  const stopMicSendGain = useCallback(() => {
    if (micMeterRafRef.current) { cancelAnimationFrame(micMeterRafRef.current); micMeterRafRef.current = null; }
    micMeterAnalyserRef.current = null;
    setMicLevel(0);
    if (previewSendCtxRef.current) {
      try { previewSendCtxRef.current.close(); } catch {}
      previewSendCtxRef.current = null;
    }
    previewSendGainNodeRef.current = null;
    if (previewRawMicTrackRef.current) {
      previewRawMicTrackRef.current.stop();
      previewRawMicTrackRef.current = null;
    }
  }, []);

  // ── Potentiomètre — volume du micro (flux envoyé) ────────────────────────
  const handlePreviewVolumeChange = useCallback((pos: number) => {
    setPreviewVolume(pos);
    previewVolumeRef.current = positionToGain(pos);
    if (!previewMutedRef.current && previewSendGainNodeRef.current) {
      previewSendGainNodeRef.current.gain.setTargetAtTime(
        previewVolumeRef.current,
        previewSendGainNodeRef.current.context.currentTime,
        0.01,
      );
    }
  }, []);

  // ── Potentiomètre — volume du retour régie ───────────────────────────────
  const handleRetourVolumeChange = useCallback((pos: number) => {
    setRetourVolume(pos);
    retourVolumeRef.current = positionToGain(pos);
    if (!retourMutedRef.current && returnGainNodeRef.current) {
      const target = retourVolumeRef.current * (intercommActive ? INTERCOMM_DUCKING_GAIN : 1);
      returnGainNodeRef.current.gain.setTargetAtTime(
        target,
        returnGainNodeRef.current.context.currentTime,
        0.01,
      );
    }
  }, [intercommActive]);

  // ── Mute toggle — flux envoyé ─────────────────────────────────────────────
  const handleTogglePreviewMute = useCallback(() => {
    const muted = !previewMutedRef.current;
    previewMutedRef.current = muted;
    setPreviewMuted(muted);

    if (previewSendGainNodeRef.current) {
      previewSendGainNodeRef.current.gain.setTargetAtTime(
        muted ? 0 : previewVolumeRef.current,
        previewSendGainNodeRef.current.context.currentTime,
        0.01,
      );
    }

    streamRef.current?.getAudioTracks().forEach(t => { t.enabled = !muted; });
    const muteMsg = muted ? 'Micro coupé (mute)' : 'Micro activé (unmute)';
    logger.info(muteMsg);
    addSdpLog({ direction: 'info', type: 'event', label: muted ? '🔇 Micro coupé (mute)' : '🎤 Micro activé (unmute)' });
  }, [addSdpLog]);

  // ── Mute toggle — retour régie ────────────────────────────────────────────
  const handleToggleRetourMute = useCallback(() => {
    const muted = !retourMutedRef.current;
    retourMutedRef.current = muted;
    setRetourMuted(muted);

    if (returnGainNodeRef.current) {
      const target = muted ? 0 : retourVolumeRef.current * (intercommActive ? INTERCOMM_DUCKING_GAIN : 1);
      returnGainNodeRef.current.gain.setTargetAtTime(
        target,
        returnGainNodeRef.current.context.currentTime,
        0.01,
      );
    }

    if (hlsVideoRef.current) {
      hlsVideoRef.current.muted = muted;
    }
    returnStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !muted; });
    const muteMsg = muted ? 'Retour coupé (mute)' : 'Retour activé (unmute)';
    logger.info(muteMsg);
    addSdpLog({ direction: 'info', type: 'event', label: muted ? '🔇 Retour coupé (mute)' : '📺 Retour activé (unmute)' });
  }, [addSdpLog, intercommActive]);

  // ── Geolocation ───────────────────────────────────────────────────────────
  const requestGeolocation = useCallback(() => {
    if (!navigator.geolocation) { setGeolocation({ loading: false, error: 'Non supporté' }); return; }
    setGeolocation({ loading: true });
    navigator.geolocation.getCurrentPosition(
      pos => setGeolocation({ loading: false, latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      err => {
        logger.warn('Géolocalisation échouée', err.message);
        setGeolocation({ loading: false, error: err.message });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);


  const handleReturnStreamReady = useCallback((stream: MediaStream, trackKind?: 'audio' | 'video') => {
    logger.info('Flux retour WebRTC prêt');
    addSdpLog({ direction: 'info', type: 'event', label: '↩ Flux retour WebRTC prêt' });
    if (!hlsVideoRef.current) return;

    // pc.ontrack déclenche cet appel une fois par track (vidéo, puis audio ~1s plus
    // tard), avec le même objet MediaStream à chaque fois. Réaffecter srcObject à un
    // stream déjà en place force un rechargement de l'élément ("play() interrupted by
    // a new load request"), donc on ne le fait que si le stream affiché a changé.
    const buildingPipeline = !returnAudioCtxRef.current && trackKind === 'audio' && stream.getAudioTracks().length > 0;

    // Tant que le pipeline volume/vu-mètre n'est pas (encore) construit, on affiche le
    // stream brut tel quel (au moins la vidéo, l'audio suivra nativement en attendant).
    if (!returnAudioCtxRef.current && !buildingPipeline && hlsVideoRef.current.srcObject !== stream) {
      hlsVideoRef.current.srcObject = stream;
      hlsVideoRef.current.play().catch((err: any) => {
        logger.warn('Lecture automatique retour bloquée', err?.message);
      });
    }

    // Pipeline Web Audio pour le volume/ducking + vu-mètre du retour, construit une
    // seule fois, dès que la piste audio est réellement là (son propre ontrack, pas
    // juste sa présence dans le stream partagé qui peut précéder l'ontrack).
    //
    // Important : on ne contrôle PAS le volume en tapant l'audio de l'élément <video>
    // via createMediaElementSource — avec un srcObject de type MediaStream, certains
    // navigateurs continuent de jouer l'audio nativement en parallèle, et le GainNode
    // n'a alors aucun effet sur ce qu'on entend (constaté : le potentiomètre ne changeait
    // rien au volume réel). À la place, comme pour le micro, on traite la piste AVANT de
    // la donner à la vidéo : la piste réellement jouée est celle qui sort du GainNode, et
    // le vu-mètre tape ce même GainNode (un vrai parallèle du son entendu).
    if (buildingPipeline) {
      const audioTrack = stream.getAudioTracks()[0];
      try {
        const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioCtxClass();
        const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
        const gain = ctx.createGain();
        gain.gain.value = retourMutedRef.current ? 0 : retourVolumeRef.current;
        const dest = ctx.createMediaStreamDestination();
        source.connect(gain);
        gain.connect(dest);
        returnAudioCtxRef.current = ctx;
        returnGainNodeRef.current  = gain;

        const meterAnalyser = ctx.createAnalyser();
        meterAnalyser.fftSize = 256;
        meterAnalyser.smoothingTimeConstant = 0.3;
        gain.connect(meterAnalyser);
        retourMeterAnalyserRef.current = meterAnalyser;
        const meterData = new Uint8Array(meterAnalyser.frequencyBinCount);
        if (retourMeterRafRef.current) { cancelAnimationFrame(retourMeterRafRef.current); retourMeterRafRef.current = null; }
        const meterLoop = () => {
          if (!retourMeterAnalyserRef.current) return;
          retourMeterAnalyserRef.current.getByteFrequencyData(meterData);
          let peak = 0;
          for (let i = 0; i < meterData.length; i++) if (meterData[i] > peak) peak = meterData[i];
          setRetourLevel(Math.round((peak / 255) * 100));
          retourMeterRafRef.current = requestAnimationFrame(meterLoop);
        };
        retourMeterRafRef.current = requestAnimationFrame(meterLoop);

        // La vidéo joue désormais la piste audio TRAITÉE (post-gain) : le potentiomètre
        // et le mute agissent réellement sur ce qui sort.
        const processedTrack = dest.stream.getAudioTracks()[0];
        const playbackStream = new MediaStream([...stream.getVideoTracks(), processedTrack]);
        hlsVideoRef.current.srcObject = playbackStream;
        hlsVideoRef.current.play().catch((err: any) => {
          logger.warn('Lecture automatique retour bloquée', err?.message);
        });

        ctx.resume().catch(() => {});
        addSdpLog({ direction: 'info', type: 'event', label: '↩ Retour : pipeline Web Audio créé (volume/ducking)' });
      } catch (e) {
        addSdpLog({ direction: 'info', type: 'event', label: `↩ Retour : pipeline Web Audio échoué : ${e}` });
        // Repli : afficher le flux brut si le pipeline n'a pas pu être construit.
        if (hlsVideoRef.current.srcObject !== stream) {
          hlsVideoRef.current.srcObject = stream;
          hlsVideoRef.current.play().catch(() => {});
        }
      }
    }
  }, [addSdpLog]);

  // ============================================================================
  // Handlers connexion
  // ============================================================================

  const handleCreateOffer = useCallback(async () => {
    if (!streamRef.current) {
      try {
        const s = await captureDeviceStream(selectedCameraRef.current, selectedMicRef.current, true, { addSdpLog });
        attachMicSendGain(s);
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (err: any) {
        logger.error("Impossible d'accéder à la caméra", err?.message);
        addSdpLog({ direction: 'received', type: 'error', label: "❌ Impossible d'accéder à la caméra" });
        return;
      }
    }
    await createDeviceOffer(webRtcRefs, webRtcCallbacks);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSdpLog]);

  // iOS audio unlock : créer l'élément pendant un geste utilisateur et
  // l'alimenter avec un MediaStream silencieux pour qu'il reste en état
  // "playing". Un simple play()/pause() consomme le token d'activation sur
  // Safari et bloque tout play() ultérieur hors geste.
  const ensureIntercommAudioElement = useCallback(() => {
    if (intercommAudioRef.current) return;

    const audio = document.createElement('audio');
    audio.autoplay = true;
    (audio as any).playsInline = true;
    document.body.appendChild(audio);
    intercommAudioRef.current = audio;

    addSdpLog({ direction: 'info', type: 'event', label: `🎙 Intercomm audio elem créé — plateforme=${isIOS ? 'iOS' : isAndroid ? 'Android' : 'autre'}` });

    try {
      // Nourrir l'élément avec du silence via AudioContext pour le garder actif
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      const silenceCtx = new AudioCtxClass();
      intercommSilenceCtxRef.current = silenceCtx;
      const dest = silenceCtx.createMediaStreamDestination();
      audio.srcObject = dest.stream;
      audio.play()
        .then(() => addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm silence play() OK — élément actif' }))
        .catch(e  => addSdpLog({ direction: 'info', type: 'event', label: `🎙 Intercomm silence play() bloqué : ${e}` }));
    } catch (e) {
      // Fallback si AudioContext indisponible
      addSdpLog({ direction: 'info', type: 'event', label: `🎙 Intercomm AudioContext indisponible, fallback muted play : ${e}` });
      audio.muted = true;
      audio.play().then(() => { audio.muted = false; }).catch(() => {});
    }
  }, [addSdpLog]);

  /**
   * `credentials` : fourni par la connexion par lien, qui dispose des valeurs
   * résolues avant que les states correspondants ne soient rendus.
   */
  const handleLogin = async (credentials?: { deviceId: string; channelUUID: string; accessToken: string }) => {
    const effectiveDeviceId    = credentials?.deviceId    ?? deviceId;
    const effectiveChannelUUID = credentials?.channelUUID ?? channelUUID;
    const effectiveToken       = credentials?.accessToken ?? accessTokenRef.current;

    // En mode lien, le token remplace le mot de passe : rien à saisir.
    const missingField = effectiveToken
      ? (!effectiveDeviceId || !effectiveChannelUUID)
      : (!effectiveDeviceId || !password || !effectiveChannelUUID);
    if (missingField) { setAuthError('Veuillez remplir tous les champs'); return; }

    setLoading(true); setAuthError('');
    if (!effectiveToken) {
      localStorage.setItem('device_id',       effectiveDeviceId);
      localStorage.setItem('device_password', password);
      localStorage.setItem('channel_uuid',    effectiveChannelUUID);
    }
    shouldReconnectRef.current = true;

    ensureIntercommAudioElement();

    logger.info(`Tentative de connexion — device: ${effectiveDeviceId}, canal: ${effectiveChannelUUID}${effectiveToken ? ' (lien)' : ''}`);

    const signalingConfig: DeviceSignalingConfig = {
      serverUrl  : SERVER_URL,
      channelUUID: effectiveChannelUUID,
      deviceId   : effectiveDeviceId,
      password   : effectiveToken ? '' : password,
      ...(effectiveToken ? { accessToken: effectiveToken } : {}),
    };

    connectDeviceToSignaling(signalingConfig, { wsRef, shouldReconnectRef }, {
      addSdpLog,

      onAuthSuccess: async (sfu?: DeviceSfuInfo) => {
        setIsAuthenticated(true);
        isAuthenticatedRef.current = true;
        setLoading(false);
        setAuthError('');
        sfuInfoRef.current = sfu ?? null;
        logger.info(`Authentification réussie${sfu ? ' (transport SFU)' : ''}`);

        try {
          releaseDeviceStream(streamRef, videoRef.current, { addSdpLog });
          const s = await captureDeviceStream(selectedCameraRef.current, selectedMicRef.current, true, { addSdpLog });

          const details = await collectActiveStreamDetails(s);
          setActiveStreamDetails(details);

          attachMicSendGain(s);
          streamRef.current = s;
          if (videoRef.current) videoRef.current.srcObject = s;

          s.getAudioTracks().forEach(t => {
            t.enabled = true;
            addSdpLog({ direction: 'info', type: 'event', label: `🎤 Piste audio activée: ${t.label}` });
          });

          previewMutedRef.current = false;
          setPreviewMuted(false);

          // Mic dédié pour l'intercomm device→régie (indépendant de la piste caméra)
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            intercommSendStreamRef.current = micStream;
            addSdpLog({ direction: 'info', type: 'event', label: '🎙 Mic intercomm device capturé' });
          } catch {
            // Fallback : clone de la piste audio principale
            const t = s.getAudioTracks()[0];
            if (t) intercommSendStreamRef.current = new MediaStream([t.clone()]);
            addSdpLog({ direction: 'info', type: 'event', label: '🎙 Mic intercomm device : fallback clone piste principale' });
          }

          if (sfu) {
            // ── Mode SFU : publication WHIP directe + retour programme WHEP ──
            // Pas de négociation P2P — la régie tire le flux depuis MediaMTX.
            addSdpLog({ direction: 'info', type: 'event', label: `📡 Mode SFU — publication WHIP sur ${sfu.path}` });
            // En SFU il n'y a pas de PeerConnection avec la régie : c'est l'état de la
            // publication WHIP qui dit si le device émet réellement.
            await publishDeviceWhip(s, sfu.whip_url, addSdpLog, (state) => {
              setConnected(state === 'connected');
              if (state !== 'connected') setIsOnAir(false);
            });
            playProgramWhep(
              sfu.program_whep_url,
              (programStream) => handleReturnStreamReady(programStream),
              addSdpLog,
            );
          } else {
            addSdpLog({ direction: 'info', type: 'event', label: "⏳ En attente de l'Offer de la régie…" });
          }
        } catch (err: any) {
          logger.error("Impossible d'accéder à la caméra après auth", err?.message);
          setAuthError("Impossible d'accéder à la caméra");
          setLoading(false);
        }
      },

      onAuthError: (reason) => {
        const msg = reason === 'already_connected'
          ? '⚠️ Cet identifiant est déjà utilisé par un autre appareil connecté.'
          : effectiveToken
            ? "Ce lien de connexion n'est plus valide. Connectez-vous avec vos identifiants."
            : 'Identifiant ou mot de passe incorrect';
        // Lien refusé : on rend la main au formulaire classique.
        if (effectiveToken) accessTokenRef.current = '';
        logger.warn('Erreur auth', msg);
        setAuthError(msg);
        setLoading(false);
        shouldReconnectRef.current = false;
        handleLogoutCleanup(false);
      },

      onOffer:        async (_from, offer, meta) => { await handleDeviceRemoteOffer(offer, meta, webRtcRefs, webRtcCallbacks); },
      onAnswer:       async (_from, answer)       => { await handleDeviceRemoteAnswer(answer, webRtcRefs, webRtcCallbacks); },
      onIceCandidate: async (_from, candidate)    => { await handleDeviceRemoteIceCandidate(candidate, webRtcRefs); },

      onReturnOffer: async (_from, offer) => {
        await handleDeviceReturnOffer(
          offer,
          pcReturnRef,
          returnStreamRef,
          iceBufReturnRef,
          wsRef,
          { addSdpLog },
          handleReturnStreamReady,
        );
      },
      onReturnIceCandidate: async (_from, candidate) => {
        await handleDeviceReturnIceCandidate(candidate, pcReturnRef, iceBufReturnRef);
      },

      onOnAir: (active) => {
        setIsOnAir(active);
      },

      // Le rôle retiré pendant que l'onglet Réalisateur est ouvert : on ramène
      // le porteur sur « Général » plutôt que de le laisser sur un onglet mort.
      onDirector: (active) => {
        setIsDirector(active);
        if (!active) {
          setActiveTab('general');
          // Deck vidé avec le rôle : le garder afficherait un pupitre périmé au
          // prochain passage en Réalisateur, avant l'arrivée du premier envoi.
          setDirectorSources([]);
          setDirectorFinalId(null);
        }
      },

      onDirectorDeck: (sources, finalId) => {
        setDirectorSources(sources);
        setDirectorFinalId(finalId);
      },

      onDeviceRejected: (msg) => {
        const errMsg = `Codec incompatible. Requis : ${msg.required_codec?.codec} ${msg.required_codec?.profile}`;
        logger.error(errMsg);
        setAuthError(errMsg);
        shouldReconnectRef.current = false;
        handleLogoutCleanup(false);
      },

      onConnectionFailed: (reason) => {
        logger.warn('Connexion au canal impossible après plusieurs tentatives', reason);
        const msg = reason
          ? 'Cette chaîne est éteinte ou indisponible.'
          : 'Connexion au canal impossible.';
        setAuthError(`⚠️ ${msg} Vérifiez que la chaîne est allumée côté régie puis réessayez.`);
        setLoading(false);
        shouldReconnectRef.current = false;
        handleLogoutCleanup(false);
      },

      onForceDisconnect: () => {
        logger.warn('Déconnecté par la régie');
        // La capture est coupée tout de suite (handleLogout) — la popup ne fait que
        // laisser 5 s au porteur pour comprendre pourquoi il est revenu au login.
        handleLogout();
        setForcedDisconnect(true);
        if (forcedDisconnectTimerRef.current) clearTimeout(forcedDisconnectTimerRef.current);
        forcedDisconnectTimerRef.current = setTimeout(() => setForcedDisconnect(false), 5000);
      },

      onSignalingStatusChange: (open) => setSignalingOpen(open),

      onIntercommOffer: async (streamId, offer) => {
        const deviceIntercommRefs: DeviceIntercommRefs = {
          pcIntercommReceiveRef,
          iceBufIntercommReceiveRef,
          intercommReceiveStreamRef,
          pcIntercommSendRef,
          iceBufIntercommSendRef,
          intercommSendStreamRef,
        };
        if (streamId === 'intercomm_regie_to_device') {
          await handleDeviceIntercommReceiveOffer(offer, deviceIntercommRefs, wsRef, {
            addSdpLog,
            onIntercommReceiveReady: (stream) => {
              const audioTracks = stream.getAudioTracks();
              addSdpLog({
                direction: 'info', type: 'event',
                label: `🎙 onIntercommReceiveReady — tracks=${audioTracks.length} ${audioTracks.map(t => `[${t.kind} enabled=${t.enabled} state=${t.readyState} label="${t.label}"]`).join(' ')}`,
              });

              const audio = intercommAudioRef.current ?? (() => {
                addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm audio elem absent (fallback création hors geste)' });
                const el = document.createElement('audio');
                el.autoplay = true;
                (el as any).playsInline = true;
                document.body.appendChild(el);
                intercommAudioRef.current = el;
                return el;
              })();

              addSdpLog({
                direction: 'info', type: 'event',
                label: `🎙 Intercomm audio avant srcObject — paused=${audio.paused} muted=${audio.muted} readyState=${audio.readyState} hasSrc=${!!audio.srcObject}`,
              });

              // Fermer le contexte silence maintenant que le vrai stream arrive
              if (intercommSilenceCtxRef.current) {
                intercommSilenceCtxRef.current.close().catch(() => {});
                intercommSilenceCtxRef.current = null;
                addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm silence AudioContext fermé' });
              }

              audio.srcObject = stream;

              addSdpLog({
                direction: 'info', type: 'event',
                label: `🎙 Intercomm srcObject assigné — paused=${audio.paused} muted=${audio.muted} readyState=${audio.readyState}`,
              });

              // L'élément jouait du silence — changer srcObject suffit normalement.
              // On n'appelle play() que s'il est effectivement en pause.
              if (audio.paused) {
                addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm audio en pause, tentative play()…' });
                audio.play()
                  .then(() => addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm play() OK' }))
                  .catch(e  => addSdpLog({ direction: 'info', type: 'event', label: `🎙 Intercomm play() bloqué : ${e}` }));
              } else {
                addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm audio déjà en lecture — OK' });
              }
            },
          });
        } else if (streamId === 'intercomm_device_to_regie') {
          await handleDeviceIntercommSendOffer(offer, deviceIntercommRefs, wsRef, {
            addSdpLog,
            onIntercommReceiveReady: () => {},
          });
        }
      },

      onIntercommIce: async (streamId, candidate) => {
        const deviceIntercommRefs: DeviceIntercommRefs = {
          pcIntercommReceiveRef,
          iceBufIntercommReceiveRef,
          intercommReceiveStreamRef,
          pcIntercommSendRef,
          iceBufIntercommSendRef,
          intercommSendStreamRef,
        };
        if (streamId === 'intercomm_regie_to_device') {
          await handleDeviceIntercommReceiveIce(candidate, deviceIntercommRefs);
        } else {
          await handleDeviceIntercommSendIce(candidate, deviceIntercommRefs);
        }
      },

      onIntercomm: (active) => {
        const audio = intercommAudioRef.current;
        addSdpLog({
          direction: 'info', type: 'event',
          label: `🎙 onIntercomm active=${active} — audio: ${audio ? `paused=${audio.paused} muted=${audio.muted} readyState=${audio.readyState} hasSrc=${!!audio.srcObject}` : 'null'}`,
        });
        setIntercommActive(active);
        // Ducking du retour pendant l'intercomm (anti-larsen).
        // On passe par le GainNode Web Audio car .volume est ignoré sur iOS/Safari.
        if (returnGainNodeRef.current) {
          const target = retourMutedRef.current ? 0 : retourVolumeRef.current * (active ? INTERCOMM_DUCKING_GAIN : 1);
          returnGainNodeRef.current.gain.setTargetAtTime(
            target,
            returnGainNodeRef.current.context.currentTime,
            0.05,
          );
        } else if (hlsVideoRef.current) {
          // Fallback si le pipeline Web Audio n'a pas pu être créé
          hlsVideoRef.current.volume = active ? INTERCOMM_DUCKING_GAIN : retourVolumeRef.current;
        }
        // Relancer play() si l'élément audio s'est mis en pause
        if (active && audio?.paused) {
          addSdpLog({ direction: 'info', type: 'event', label: '🎙 onIntercomm: audio en pause, tentative play()…' });
          audio.play()
            .then(() => addSdpLog({ direction: 'info', type: 'event', label: '🎙 onIntercomm: play() OK' }))
            .catch(e  => addSdpLog({ direction: 'info', type: 'event', label: `🎙 onIntercomm: play() bloqué : ${e}` }));
        }
      },
    });
  };

  const handleLogoutCleanup = useCallback((voluntary: boolean) => {
    shouldReconnectRef.current = false;
    stopDeviceWhip();
    stopProgramWhep();
    sfuInfoRef.current = null;
    closeDevicePeerConnection(webRtcRefs, { addSdpLog });
    closeDeviceSignaling({ wsRef });
    releaseDeviceStream(streamRef, videoRef.current, { addSdpLog });
    stopMicSendGain();
    closeDeviceReturnPeerConnection(pcReturnRef, iceBufReturnRef, { addSdpLog });
    if (retourMeterRafRef.current) { cancelAnimationFrame(retourMeterRafRef.current); retourMeterRafRef.current = null; }
    retourMeterAnalyserRef.current = null;
    setRetourLevel(0);
    if (returnAudioCtxRef.current) {
      returnAudioCtxRef.current.close().catch(() => {});
      returnAudioCtxRef.current = null;
      returnGainNodeRef.current  = null;
    }
    closeDeviceIntercomm(
      { pcIntercommReceiveRef, iceBufIntercommReceiveRef, intercommReceiveStreamRef, pcIntercommSendRef, iceBufIntercommSendRef, intercommSendStreamRef },
      { addSdpLog },
    );
    if (intercommSilenceCtxRef.current) {
      intercommSilenceCtxRef.current.close().catch(() => {});
      intercommSilenceCtxRef.current = null;
    }
    intercommAudioRef.current?.pause();
    if (intercommAudioRef.current) {
      intercommAudioRef.current.srcObject = null;
      intercommAudioRef.current.remove();
      intercommAudioRef.current = null;
    }
    intercommSendStreamRef.current?.getTracks().forEach(t => t.stop());
    intercommSendStreamRef.current = null;
    setConnected(false);
    setSignalingOpen(false);
    setIsOnAir(false);
    // Le rôle est accordé par la régie pour la session en cours : la WS coupée,
    // il retombe. La régie le réémettra si elle le veut à la reconnexion.
    setIsDirector(false);
    setActiveTab('general');

    if (voluntary) {
      logger.info('Déconnexion volontaire');
      setIsAuthenticated(false);
      isAuthenticatedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSdpLog]);

  const handleLogout = () => {
    // Session ouverte par lien : on repart sur la saisie du token, pas sur le
    // formulaire identifiant/mot de passe dont ce porteur n'a rien à faire.
    // Le champ est prérempli avec le token courant — se reconnecter ne demande
    // qu'un clic, tout en laissant la possibilité d'en saisir un autre.
    const previousToken  = accessTokenRef.current;
    const wasLinkSession = !!previousToken;

    handleLogoutCleanup(true);
    localStorage.removeItem('device_id');
    localStorage.removeItem('device_password');
    localStorage.removeItem('channel_uuid');
    // Le lien ne doit pas rebrancher la connexion automatique juste après une
    // déconnexion volontaire : on revient au formulaire.
    accessTokenRef.current = '';
    autoLoginStartedRef.current = true;
    setResolvingAccess(false);
    setAuthError('');
    // Token complet (UUID chaîne + 5 caractères) : c'est ce que le porteur a reçu,
    // et ça reste autonome si la page est rouverte sans l'UUID dans l'URL.
    setTokenInput(previousToken ? buildDeviceAccessUuid(channelUUIDRef.current, previousToken) : '');
    setTokenFormVisible(wasLinkSession);
    addSdpLog({ direction: 'info', type: 'event', label: 'Déconnexion volontaire' });
  };

  /**
   * Reconnexion depuis la page de saisie du token. Accepte le token seul
   * (5 caractères), l'UUID complet du lien, ou le lien collé en entier.
   */
  const handleTokenSubmit = async () => {
    const raw  = tokenInput.trim();
    const last = raw.split(/[/?#]/).filter(Boolean).pop() ?? '';

    const target = last.length === DEVICE_ACCESS_TOKEN_LENGTH
      ? { channelUuid: channelUUIDRef.current, accessToken: last }
      : parseDeviceAccessUuid(last);

    if (!target?.accessToken || !target.channelUuid) {
      setAuthError('Token de connexion invalide.');
      return;
    }

    setLoading(true);
    setAuthError('');
    try {
      const device = await resolveDeviceAccessToken(SERVER_URL, target.channelUuid, target.accessToken);
      if (!device) {
        setLoading(false);
        setAuthError("Ce token de connexion n'est plus valide.");
        return;
      }
      setDeviceId(device.device_id);
      deviceIdRef.current = device.device_id;
      setDeviceLabel(device.label);
      accessTokenRef.current = target.accessToken;
      setChannelUUID(target.channelUuid);
      channelUUIDRef.current = target.channelUuid;
      // tokenFormVisible reste vrai : si l'authentification échoue, c'est encore
      // cette page — et non le formulaire identifiant — qu'il faut réafficher.
      await handleLogin({
        deviceId   : device.device_id,
        channelUUID: target.channelUuid,
        accessToken: target.accessToken,
      });
    } catch (err: any) {
      setLoading(false);
      setAuthError(`Chaîne injoignable (${err?.message ?? err}).`);
    }
  };

  // ============================================================================
  // Connexion par lien (/webrtc/{uuidChaine}{token})
  // ============================================================================

  useEffect(() => {
    const token = parsedUrlUuid?.accessToken;
    if (!token) { setResolvingAccess(false); return; }

    let cancelled = false;

    const fallbackToLoginForm = (message: string) => {
      if (cancelled) return;
      logger.warn(`Connexion par lien impossible — ${message}`);
      setAuthError(message);
      // On propose la partie « chaîne » du lien, mais le champ reste modifiable :
      // si le découpage 16+5 était erroné, l'utilisateur peut le corriger.
      setChannelUUID(parsedUrlUuid.channelUuid);
      channelUUIDRef.current = parsedUrlUuid.channelUuid;
      setResolvingAccess(false);
    };

    (async () => {
      try {
        const device = await resolveDeviceAccessToken(SERVER_URL, parsedUrlUuid.channelUuid, token);
        if (cancelled) return;

        if (!device) {
          fallbackToLoginForm("Ce lien de connexion n'est plus valide. Connectez-vous avec vos identifiants.");
          return;
        }

        logger.info(`Lien reconnu — device ${device.device_id} (${device.label})`);
        setDeviceId(device.device_id);
        deviceIdRef.current    = device.device_id;
        setDeviceLabel(device.label);
        accessTokenRef.current = token;
        setChannelUUID(parsedUrlUuid.channelUuid);
        channelUUIDRef.current = parsedUrlUuid.channelUuid;
        setPendingAutoLogin({ deviceId: device.device_id, channelUUID: parsedUrlUuid.channelUuid, accessToken: token });
      } catch (err: any) {
        fallbackToLoginForm(`Chaîne injoignable (${err?.message ?? err}). Connectez-vous avec vos identifiants.`);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La connexion capture immédiatement la caméra : on attend que l'énumération
  // ait désigné la caméra frontale, sinon on partirait sur le périphérique par défaut.
  useEffect(() => {
    if (!pendingAutoLogin || autoLoginStartedRef.current) return;
    if (!loginCamerasReady && !loginCamerasError) return;
    autoLoginStartedRef.current = true;
    // resolvingAccess reste vrai jusqu'à l'auth_success (ou l'échec) : sinon le
    // formulaire de connexion apparaîtrait le temps du handshake.
    handleLogin(pendingAutoLogin);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoLogin, loginCamerasReady, loginCamerasError]);

  // ── Relevé périodique de la qualité du réseau montant ──────────────────────
  //
  // Toutes les 2 s : assez pour voir une dégradation arriver, assez peu pour que
  // getStats() ne pèse pas sur un téléphone qui encode déjà de la vidéo. Avec
  // trois mesures exigées pour monter d'un cran, une embellie met environ six
  // secondes à s'afficher — et c'est voulu.
  //
  // La PeerConnection est REDEMANDÉE à chaque tour : celle du WHIP est recréée à
  // chaque republication (changement de caméra, reprise après coupure), et une
  // référence gardée observerait une connexion morte sans le savoir.
  useEffect(() => {
    if (!connected) {
      // Le lien est tombé : on efface la mesure au lieu de laisser à l'écran un
      // niveau qui ne décrit plus rien.
      networkStatsRef.current = null;
      setNetworkQuality(initialNetworkQualityState);
      return;
    }

    let cancelled = false;
    // Rythme d'envoi à la régie, indépendant du rythme de mesure : on annonce à
    // chaque CHANGEMENT de niveau, et à défaut toutes les 5 mesures (10 s). Sans
    // ce rappel périodique, une régie qui arrive après coup resterait sans rien
    // jusqu'au prochain changement — qui peut ne jamais venir sur un bon lien.
    let sinceLastReport = 0;

    const tick = async () => {
      const pc = getDeviceWhipPeerConnection() ?? pcRef.current;
      if (!pc) return;
      const sample = await sampleNetworkQuality(pc, networkStatsRef);
      if (cancelled || !sample) return;

      setNetworkQuality(prev => {
        const next = applySample(prev, sample);
        sinceLastReport += 1;
        if (next.level !== null && (next.level !== prev.level || sinceLastReport >= 5)) {
          sinceLastReport = 0;
          // Hors de l'updater par principe — StrictMode les rejoue — mais ici
          // l'envoi est idempotent côté régie, qui écrase la valeur précédente.
          sendNetworkQuality(wsRef, next.level, next.sample);
        }
        return next;
      });
    };

    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [connected]);

  // Tout échec de la connexion automatique (lien révoqué, chaîne éteinte, caméra
  // inaccessible…) passe par authError : c'est le signal qui rend le formulaire.
  useEffect(() => {
    if (authError) setResolvingAccess(false);
  }, [authError]);

  // Connexion automatique = aucun geste utilisateur, donc l'élément audio de
  // l'intercomm reste bloqué sur iOS/Safari. On le crée au premier contact avec
  // la page, quel qu'il soit.
  useEffect(() => {
    if (!parsedUrlUuid?.accessToken) return;
    const unlock = () => ensureIntercommAudioElement();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('touchstart',  unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart',  unlock);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureIntercommAudioElement]);

  // ── Caméra / micro ────────────────────────────────────────────────────────

const handleCameraChange = useCallback(async (cameraId: string) => {
  setSelectedCamera(cameraId);
  selectedCameraRef.current = cameraId;
  if (!isAuthenticatedRef.current) return;

  await changeDeviceCamera(
    cameraId, selectedMicRef.current, true,
    pcRef.current, streamRef, videoRef.current, { addSdpLog },
    (newStream) => { if (videoRef.current) videoRef.current.srcObject = newStream; }
  );
  if (streamRef.current) {
    attachMicSendGain(streamRef.current);
    if (pcRef.current?.connectionState === 'connected') {
      const at = streamRef.current.getAudioTracks()[0];
      if (at) await replaceDeviceTrack(pcRef.current, at, { addSdpLog });
    }
  }

  // Mode SFU : replaceTrack sur la PC WHIP, pas de négociation P2P
  if (isDeviceWhipActive()) {
    if (streamRef.current) await replaceDeviceWhipTracks(streamRef.current);
    return;
  }

  // Ne renégocier que si la PC n'est pas connectée (replaceTrack a échoué
  // ou il n'y a pas encore de connexion). Si la PC est connected,
  // replaceTrack suffit — pas besoin d'une nouvelle offer.
  const state = pcRef.current?.connectionState;
  if (state !== 'connected') {
    logger.info('Renégociation nécessaire: PC non connectée');
    addSdpLog({
      direction: 'info', type: 'event',
      label: `🔄 Renégociation: PC état=${state ?? 'null'}`,
    });
    await handleCreateOffer();
  }
}, [addSdpLog, handleCreateOffer, attachMicSendGain]);

  const handleMicChange = useCallback(async (micId: string) => {
    setSelectedMic(micId);
    selectedMicRef.current = micId;
    if (!isAuthenticatedRef.current) return;
    try {
      await changeDeviceMic(micId, true, pcRef.current, streamRef, { addSdpLog });
      if (streamRef.current) {
        attachMicSendGain(streamRef.current);
        if (pcRef.current?.connectionState === 'connected') {
          const at = streamRef.current.getAudioTracks()[0];
          if (at) await replaceDeviceTrack(pcRef.current, at, { addSdpLog });
        }
        // Mode SFU : répercuter le nouveau micro sur la PC WHIP
        if (isDeviceWhipActive()) await replaceDeviceWhipTracks(streamRef.current);
      }
    } catch (err: any) {
      logger.error('Changement micro échoué', err?.message);
      addSdpLog({ direction: 'received', type: 'error', label: `❌ Changement micro échoué: ${err.message}` });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSdpLog]);

  // ============================================================================
  // Render
  // ============================================================================

  const platformInfo = getPlatformInfo();
  const filteredMics = availableMics.filter(m => m.label && m.label.trim().toLowerCase() !== 'default');

  // ── Potentiomètre — drag pour ajuster la position (0-100, bas → haut) ───────
  const updatePotentiometerPosition = (
    e: React.PointerEvent<HTMLDivElement>,
    onChange: (pos: number) => void,
  ) => {
    const rect   = e.currentTarget.getBoundingClientRect();
    const ratio  = 1 - (e.clientY - rect.top) / rect.height;
    const rawPos = Math.max(0, Math.min(100, Math.round(ratio * 100)));
    const pos    = Math.abs(rawPos - DEFAULT_VOLUME_POSITION) <= POTENTIOMETER_SNAP_RANGE
      ? DEFAULT_VOLUME_POSITION
      : rawPos;
    onChange(pos);
  };

  const handlePotentiometerPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    onChange: (pos: number) => void,
  ) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updatePotentiometerPosition(e, onChange);
  };

  const handlePotentiometerPointerMove = (
    e: React.PointerEvent<HTMLDivElement>,
    onChange: (pos: number) => void,
  ) => {
    if (e.buttons !== 1) return;
    updatePotentiometerPosition(e, onChange);
  };

  // ── Potentiomètre de volume (fader vertical interactif) ──────────────────
  const renderVolumePotentiometer = (
    volume: number,
    level: number,
    onVolumeChange: (pos: number) => void,
    muted: boolean,
    onToggleMute: () => void,
    muteTitle: string,
    icon: React.ReactNode,
  ) => {
    const isAtZeroDb    = Math.abs(volume - DEFAULT_VOLUME_POSITION) < 0.01;
    const isAboveZeroDb = !isAtZeroDb && volume > DEFAULT_VOLUME_POSITION;
    const gainColor     = isAtZeroDb   ? POTENTIOMETER_COLOR_ZERO
                        : isAboveZeroDb ? POTENTIOMETER_COLOR_ABOVE
                        :                 POTENTIOMETER_COLOR_BELOW;

    return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', alignSelf: 'stretch', flexShrink: 0 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        <div
          onPointerDown={e => handlePotentiometerPointerDown(e, onVolumeChange)}
          onPointerMove={e => handlePotentiometerPointerMove(e, onVolumeChange)}
          style={{ position: 'relative', width: POTENTIOMETER_TRACK_W, height: '100%', background: '#e5e7eb', borderRadius: 8, cursor: 'ns-resize', touchAction: 'none' }}
        >
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: `${level}%`,
            background: level > 80 ? '#ef4444' : level > 50 ? '#f59e0b' : '#22c55e',
            borderRadius: 8,
            transition: 'height 0.05s linear',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', left: '50%', bottom: `${volume}%`,
            transform: 'translate(-50%, 50%)',
            width: POTENTIOMETER_TRACK_W + POTENTIOMETER_HANDLE_OVF * 2, height: POTENTIOMETER_HANDLE_H, borderRadius: 1,
            background: gainColor, border: `1px solid ${gainColor}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            transition: 'background 0.15s ease, border-color 0.15s ease',
          }} />
        </div>

        <div style={{ position: 'relative', width: POTENTIOMETER_MARKS_W, height: '100%', flexShrink: 0 }}>
          {DB_MARKS.map((db, i) => {
            // Marks statiques (indépendants de la position de la poignée) :
            // +6 dB toujours rouge, 0 dB toujours jaune, le reste toujours gris.
            const markColor = db === 6 ? POTENTIOMETER_COLOR_ABOVE
                             : db === 0 ? POTENTIOMETER_COLOR_ZERO
                             :            '#cbd5e1';
            const textColor = db === 6 ? POTENTIOMETER_COLOR_ABOVE
                             : db === 0 ? POTENTIOMETER_COLOR_ZERO
                             :            '#666';

            return (
              <div
                key={db}
                style={{
                  position: 'absolute', left: 0, bottom: `${((DB_MARKS.length - 1 - i) / (DB_MARKS.length - 1)) * 100}%`,
                  transform: 'translateY(50%)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <span style={{ width: 6, height: 2, background: markColor, borderRadius: 1, flexShrink: 0, transition: 'background 0.15s ease' }} />
                <span style={{ fontSize: 10, color: textColor, fontWeight: 600, whiteSpace: 'nowrap', width: POTENTIOMETER_MARKS_W - 10, textAlign: 'right', transition: 'color 0.15s ease' }}>
                  {db > 0 ? `+${db}` : db}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <button
        onClick={onToggleMute}
        title={muteTitle}
        style={{
          width: 36, height: 36, borderRadius: '50%',
          border: `2px solid ${muted ? '#ef4444' : '#22c55e'}`,
          background: muted ? '#fef2f2' : '#f0fdf4',
          color: muted ? '#ef4444' : '#16a34a',
          fontSize: 16, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'all 0.15s ease',
        }}
      >
        {icon}
      </button>
    </div>
    );
  };

  // ── Barres de qualité réseau ──────────────────────────────────────────────
  //
  // Quatre barres croissantes, comme sur un téléphone : celles atteintes sont
  // colorées, les autres restent en creux. Un dessin plutôt qu'un chiffre —
  // « 2,3 Mb/s » ne dit rien à qui filme, quatre barres sur quatre si.
  //
  // Le niveau n'est affiché QUE si le lien est établi et qu'une mesure existe :
  // pendant les premières secondes il n'y a rien à montrer, et des barres grises
  // seraient lues comme « aucun réseau » alors qu'on ne sait simplement pas
  // encore. Dans ce cas l'icône n'est pas rendue du tout.
  const renderNetworkQuality = () => {
    const level = networkQuality.level;
    if (!connected || level === null) return null;

    // Barres pleines en blanc : elles ne s'affichent QUE dans le badge « Connecté »,
    // sur fond vert opaque, où les couleurs de niveau (vert/jaune) se noyaient. Le
    // niveau reste lisible au NOMBRE de barres, pas à leur teinte — les barres
    // éteintes sont le même blanc à 45 %. Même teinte que l'appli device
    // (remote-device-front), pour que les deux badges se lisent pareil.
    return (
      <span
        title={`${QUALITY_LABELS[level]} — ${describeSample(networkQuality.sample)}`}
        aria-label={`${QUALITY_LABELS[level]} (${level} sur 4)`}
        role="img"
        style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 13, marginLeft: 2 }}
      >
        {[1, 2, 3, 4].map(bar => (
          <span
            key={bar}
            style={{
              display     : 'block',
              width       : 3,
              height      : 3 + bar * 2.5,
              borderRadius: 1,
              background  : bar <= level ? 'rgb(255,255,255)' : 'rgba(255,255,255,0.45)',
              transition  : 'background 0.3s',
            }}
          />
        ))}
      </span>
    );
  };

  // ── Étiquette d'état du lien avec la régie ────────────────────────────────
  // Même forme que le badge « Chaîne active » du dashboard de la chaîne.
  // Le halo (anneau qui pulse) signale un état transitoire : en attente d'être pris
  // à l'antenne, ou lien coupé et reconnexion en cours.
  const renderConnectionBadge = () => {
    const state: 'connected' | 'waiting' | 'lost' =
      connected ? 'connected' : signalingOpen ? 'waiting' : 'lost';

    const { label, color, background, halo } = {
      // Fond et texte imposés (rgb(11,51,63) / rgb(49,199,111)) : fond opaque, pas un
      // vert transparenté — ne pas remplacer par un token de thème.
      connected: { label: 'Connecté', color: 'rgb(255,255,255)', background: 'rgb(49,199,111)',  halo: null },
      // Rouge opaque : « En attente » annonçait l'inverse de ce qu'il décrit — le
      // même vert que « Connecté », le halo rouge étant la seule différence.
      waiting:   { label: 'En attente', color: 'rgb(255,255,255)', background: 'rgb(239,68,68)',   halo: 'rgba(239,68,68,0.60)' },
      lost:      { label: 'Connexion perdue',    color: '#dc2626', background: 'rgba(239,68,68,0.15)', halo: 'rgba(239,68,68,0.60)' },
    }[state];

    return (
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginTop: 6, padding: '5px 12px', borderRadius: 20,
          fontSize: 13, fontWeight: 500,
          color, background,
          ...(halo ? { ['--halo' as any]: halo, animation: 'badgeHalo 1.8s ease-out infinite' } : {}),
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
        {label}
        {renderNetworkQuality()}
      </span>
    );
  };

  // ── Popup « déconnecté par la régie » ─────────────────────────────────────
  // Rendue dans tous les écrans : handleLogout ramène immédiatement au login,
  // c'est donc cet écran-là qui l'affiche pendant les 5 secondes.
  const forcedDisconnectPopup = forcedDisconnect && (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: '28px 32px', maxWidth: 360, width: '100%',
        textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔌</div>
        <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111' }}>Vous avez été déconnecté</p>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: '#666' }}>
          La régie a mis fin à votre connexion.
        </p>
      </div>
    </div>
  );

  // ── Écran d'attente : vérification du lien de connexion ───────────────────
  // Affiché à la place du formulaire tant que le microservice n'a pas tranché,
  // pour ne pas faire apparaître une page de login qui va disparaître seule.
  if (!isAuthenticated && resolvingAccess) {
    return (
      <div style={{ minHeight: '88vh', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', justifyContent: 'center', padding: '10px 20px 20px 20px' }}>
        <div style={{ backgroundImage: 'url(/images/RemoteDeviceBackground.png)', backgroundSize: '100% 100%', borderRadius: 16, padding: 30, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <img src="/images/logo.svg" alt="logo" style={{ width: 180, height: 'auto' }} />
          <p style={{ color: '#333', fontSize: 16, fontWeight: 600, margin: 0 }}>🔄 Connexion en cours…</p>
          <p style={{ color: '#666', fontSize: 13, margin: 0, textAlign: 'center' }}>
            Vérification de votre lien auprès de la chaîne.
          </p>
        </div>
        {forcedDisconnectPopup}
      </div>
    );
  }

  // ── Écran de login ────────────────────────────────────────────────────────
  if (!isAuthenticated) {

    const loginFrontCam = availableCameras.find(c => /front|avant|user|facingMode=user/i.test(c.label));
    const loginRearCam  = availableCameras.find(c => /back|rear|arrière|environment|facingMode=environment/i.test(c.label));

    return (
      <div style={{ minHeight: '88vh', background: 'linear-gradient(135deg,#667eea,#764ba2)', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', justifyContent: 'center', padding: '10px 20px 20px 20px' }}>
        <div style={{ backgroundImage: 'url(/images/RemoteDeviceBackground.png)', backgroundSize: '100% 100%', borderRadius: 16, padding: 10, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          {forcedDisconnectPopup}
          <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
<div
  onClick={() => window.open('https://beta.kleonpro.tv', '_blank')}
  style={{
    width: 400,
    height: 'auto',
    padding: 10,
    background: 'rgba(255,255,255,0.15)',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }}
>
  <img
    src="/images/logo.svg"
    alt="logo"
    style={{ width: 180, height: 'auto' }}
  />
  <img
    src="/images/logo3.png"
    alt="logo3"
    style={{ width: 300, height: 'auto' }}
  />
</div>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 'bold', color: '#333', margin: 0 }}>Connexion Device</h1>
            <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
              {isIOS ? '📱 iOS' : isAndroid ? '🤖 Android' : '💻 Autre'} · {platformInfo.browser}
            </p>
          </div>

          {authError && (
            <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: 8, padding: 12, marginBottom: 20, color: '#c33', fontSize: 13, whiteSpace: 'pre-line' }}>
              ⚠️ {authError}
            </div>
          )}

          {/* Mode token : le porteur d'un lien ne saisit que son token — l'UUID de
              la chaîne est déjà connu, et il n'a ni identifiant ni mot de passe. */}
          {tokenFormVisible && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>Token de connexion</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                  <Icon name="lockedPadlock2" color="#593BD9" hoverable={false} clickable={false} />
                </div>
                <input
                  type="text"
                  value={tokenInput}
                  onChange={e => { setTokenInput(e.target.value); setAuthError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleTokenSubmit(); }}
                  placeholder="aBcD1234EfGh5678wXyZv"
                  disabled={loading}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ flex: 1, minWidth: 0, color: '#111', background: 'white', padding: '12px 16px', border: '2px solid #ddd', borderRadius: 8, fontSize: 16, boxSizing: 'border-box', fontFamily: 'monospace' }}
                />
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>
                Token reçu avec votre lien de connexion (vous pouvez aussi coller le lien entier).
              </p>
            </div>
          )}

          {/* UUID — ligne entière */}
          {!tokenFormVisible && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>UUID du canal</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                <Icon name="radioSignal2" color="#593BD9" hoverable={false} clickable={false} />
              </div>
              <input
                type="text"
                value={channelUUID}
                onChange={e => setChannelUUID(e.target.value)}
                placeholder="aBcD1234EfGh5678"
                disabled={loading}
                // Verrouillé quand l'URL ne portait que l'UUID de la chaîne ; laissé
                // modifiable après l'échec d'un lien, dont le découpage est une déduction.
                readOnly={!!uuidFromUrl && !parsedUrlUuid?.accessToken}
                style={{ flex: 1, color: '#111', background: (uuidFromUrl && !parsedUrlUuid?.accessToken) ? '#f5f5f5' : 'white', padding: '12px 16px', border: '2px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'monospace' }}
              />
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>UUID de la chaîne (visible dans les paramètres de la régie)</p>
          </div>
          )}

          {/* Identifiant + Mot de passe — côte à côte */}
          {!tokenFormVisible && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>Identifiant</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                  <Icon name="user" color="#593BD9" hoverable={false} clickable={false} />
                </div>
                <input
                  type="text"
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value)}
                  placeholder="phone-001"
                  disabled={loading}
                  style={{ flex: 1, minWidth: 0, color: '#111', background: 'white', padding: '12px 16px', border: '2px solid #ddd', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>Mot de passe</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                  <Icon name="lockedPadlock2" color="#593BD9" hoverable={false} clickable={false} />
                </div>
                <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                    style={{ color: '#111', background: 'white', width: '100%', padding: '12px 44px 12px 16px', border: '2px solid #ddd', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#000', display: 'flex', alignItems: 'center' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}

          <div style={{ marginBottom: 16 }}>
            {loginCamerasError ? (
              <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 13, color: '#c2410c' }}>
                ⚠️ {loginCamerasError}
              </div>
            ) : !loginCamerasReady ? (
              <div style={{ padding: '10px 12px', background: '#f9fafb', border: '2px solid #ddd', borderRadius: 8, fontSize: 13, color: '#999' }}>
                🔄 Chargement des caméras...
              </div>
            ) : availableCameras.length === 0 ? (
              <div style={{ padding: '10px 12px', background: '#f9fafb', border: '2px solid #ddd', borderRadius: 8, fontSize: 13, color: '#999' }}>
                Aucune caméra détectée
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `1fr${loginFrontCam ? ' 52px' : ''}${loginRearCam ? ' 52px' : ''}`,
                gridTemplateRows: 'auto auto',
                gap: '6px 8px',
                alignItems: 'stretch',
              }}>
                <label style={{ gridColumn: 1, gridRow: 1, fontSize: 14, fontWeight: 600, color: '#333', alignSelf: 'end' }}>
                  📹 Caméra
                </label>
                <select
                  value={selectedCamera}
                  onChange={e => { setSelectedCamera(e.target.value); selectedCameraRef.current = e.target.value; }}
                  disabled={loading}
                  style={{ gridColumn: 1, gridRow: 2, width: '100%', padding: '10px 12px', border: '2px solid #ddd', borderRadius: 8, fontSize: 14, background: 'white', color: '#111' }}
                >
                  {availableCameras.map(c => <option key={c.deviceId} value={c.deviceId}>{c.label}</option>)}
                </select>
                {loginFrontCam && (
                  <button
                    onClick={() => { setSelectedCamera(loginFrontCam.deviceId); selectedCameraRef.current = loginFrontCam.deviceId; }}
                    disabled={loading}
                    style={{
                      gridColumn: 2, gridRow: '1 / 3',
                      border: '2px solid #6366f1', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px',
                      background:  selectedCamera === loginFrontCam.deviceId ? '#6366f1' : 'white',
                      color:       selectedCamera === loginFrontCam.deviceId ? 'white'   : '#111',
                    }}
                  ><span style={{ fontSize: 20 }}>🤳</span><span style={{ color: selectedCamera === loginFrontCam.deviceId ? 'white' : '#111' }}>Avant</span></button>
                )}
                {loginRearCam && (
                  <button
                    onClick={() => { setSelectedCamera(loginRearCam.deviceId); selectedCameraRef.current = loginRearCam.deviceId; }}
                    disabled={loading}
                    style={{
                      gridColumn: loginFrontCam ? 3 : 2, gridRow: '1 / 3',
                      border: '2px solid #6366f1', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px',
                      background:  selectedCamera === loginRearCam.deviceId ? '#6366f1' : 'white',
                      color:       selectedCamera === loginRearCam.deviceId ? 'white'   : '#111',
                    }}
                  ><span style={{ fontSize: 20 }}>📷</span><span style={{ color: selectedCamera === loginRearCam.deviceId ? 'white' : '#111' }}>Arrière</span></button>
                )}
              </div>
            )}
          </div>

          {loginCamerasReady && availableMics.filter(m => m.label && m.label.trim().toLowerCase() !== 'default').length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>
                🎤 Micro
              </label>
              <select
                value={selectedMic}
                onChange={e => { setSelectedMic(e.target.value); selectedMicRef.current = e.target.value; }}
                disabled={loading}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #ddd', borderRadius: 8, fontSize: 14, background: 'white', color: '#111' }}
              >
                {availableMics
                  .filter(m => m.label && m.label.trim().toLowerCase() !== 'default')
                  .map(m => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
              </select>
            </div>
          )}

          <button
            onClick={() => (tokenFormVisible ? handleTokenSubmit() : handleLogin())}
            disabled={loading || (tokenFormVisible
              ? !tokenInput.trim()
              : (!deviceId || !password || !channelUUID))}
            style={{ width: '100%', padding: '14px 20px', background: loading ? '#ccc' : 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8 }}
          >
            {loading ? '🔄 Connexion...' : '🔐 Se connecter'}
          </button>
        </div>
      </div>
    );
  }

  // ── App principale ────────────────────────────────────────────────────────

    // <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#667eea,#764ba2)', padding: 20 }}>
      // <div style={{ maxWidth: 800, margin: '0 auto',  background: '/images/RemoteDeviceBackground.png' }}>

  return (
  
      <div style={{ minHeight: '88vh', background: 'linear-gradient(135deg,#667eea,#764ba2)', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', justifyContent: 'center', padding: '10px 20px 20px 20px' }}>
        <div style={{ backgroundImage: 'url(/images/RemoteDeviceBackground.png)', backgroundSize: '100% 100%', borderRadius: 16, padding: 10, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
  

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div 
          onClick={() => window.open('https://beta.kleonpro.tv', '_blank')}
           style={{
    width: 400,
    height: 'auto',
    padding: 10,
    background: 'rgba(255,255,255,0.15)',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }}>
            {/* Logo animé retiré ici : il mangeait la hauteur utile au profit des vidéos.
                Il reste sur l'écran de connexion, où la place ne manque pas. */}
            <img src="/images/logo3.png" alt="logo2" style={{ width: 300, height: 'auto' }} />
			</div>
        </div>

        <div style={{ color: '#111', background: 'rgba(255,255,255,0.95)', borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {/* Onglets réservés au rôle Réalisateur : hors rôle, la carte garde
              exactement son ancien contenu, sans barre d'onglets. */}
          {isDirector && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
              {([{ id: 'general', label: 'Général' }, { id: 'director', label: '🎬 Réalisateur' }] as const).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '8px 14px', border: 'none', background: 'transparent',
                    borderBottom: activeTab === tab.id ? '3px solid #6366f1' : '3px solid transparent',
                    marginBottom: -2,
                    color: activeTab === tab.id ? '#6366f1' : '#6b7280',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >{tab.label}</button>
              ))}
            </div>
          )}

          {activeTab === 'general' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 'bold', color: '#333', margin: 0 }}>📱 {deviceLabel || deviceId}</h1>
                {renderConnectionBadge()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setShowInfoPanel(true); requestGeolocation(); }}
                  style={{ padding: '8px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >ℹ️ Infos</button>
                <button
                  onClick={handleLogout}
                  style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 'bold', cursor: 'pointer' }}
                >🚪 Déco</button>
              </div>
            </div>
          ) : (
            /* Mini-deck : un bouton par source du pupitre régie. Le clic demande le
               passage en final ; l'état affiché n'est jamais présumé localement, il
               attend le `director_deck` suivant — sans quoi un bouton refusé par la
               régie mentirait sur ce qui est réellement à l'antenne. */
            <div>
              {directorSources.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280', textAlign: 'center', padding: '12px 0' }}>
                  En attente du deck de la régie…
                </p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {directorSources.map(source => {
                    const isFinal = source.id === directorFinalId;
                    return (
                      <button
                        key={source.id}
                        onClick={() => sendDirectorTake(wsRef, source.id)}
                        style={{
                          padding: '14px 12px',
                          background: isFinal ? '#dc2626' : '#6366f1',
                          color: 'white', border: 'none',
                          boxShadow: isFinal ? '0 0 0 3px rgba(220,38,38,0.35)' : 'none',
                          borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                          textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
                          minWidth: 0,
                        }}
                      >
                        {/* Le numéro est celui du pupitre, pas celui de la liste :
                            les cases vides du deck ne sont pas transmises. */}
                        <span style={{
                          flexShrink: 0, width: 22, height: 22, borderRadius: 6,
                          background: 'rgba(255,255,255,0.22)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12,
                        }}>{source.index + 1}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {source.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Rien à dire quand une source est à l'antenne : le bouton rouge se
                  suffit. Le cas sans final, lui, mérite un mot — sinon l'absence de
                  bouton actif passe pour un bug. */}
              {!directorFinalId && (
                <p style={{ margin: '12px 0 0', fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                  Aucune source du deck à l’antenne (scène ou sortie composée).
                </p>
              )}
            </div>
          )}
        </div>

        <div style={{ background: 'rgba(0,0,0,0.95)', borderRadius: 16, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ── Retour régie ─────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gridTemplateRows: 'auto auto', columnGap: POTENTIOMETER_COL_GAP, rowGap: 6 }}>
              <h3 style={{ gridColumn: 1, gridRow: 1, fontSize: 14, fontWeight: 600, color: '#ffffff', margin: 0, textAlign: 'center' }}>Retour régie</h3>

              <div style={{ gridColumn: 1, gridRow: 2, minWidth: 0 }}>
                <div
                  ref={retourContainerRef}
                  onClick={() => toggleFullscreen('retour')}
                  style={{
                    background: '#000', borderRadius: isRetourFullscreen ? 0 : 12, overflow: 'hidden', cursor: 'pointer',
                    position: isRetourFullscreen ? 'fixed' : 'relative',
                    inset: isRetourFullscreen ? 0 : undefined,
                    zIndex: isRetourFullscreen ? 9999 : undefined,
                    aspectRatio: isRetourFullscreen ? undefined : '16 / 9',
                    width: '100%', height: isRetourFullscreen ? '100dvh' : undefined,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <video
                    ref={hlsVideoRef} autoPlay playsInline controls={false}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                  />
                  {isRetourFullscreen && (
                    <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.65)', fontSize: 12, padding: '6px 14px', borderRadius: 20, pointerEvents: 'none', userSelect: 'none' }}>
                      Toucher pour quitter le plein écran
                    </div>
                  )}
                </div>
              </div>

              <div style={{ gridColumn: 2, gridRow: 2, display: 'flex' }}>
                {renderVolumePotentiometer(
                  retourVolume,
                  retourLevel,
                  handleRetourVolumeChange,
                  retourMuted,
                  handleToggleRetourMute,
                  retourMuted ? 'Réactiver' : 'Couper le retour',
                  retourMuted ? <VolumeX color="red" /> : <Volume2 color="green" />,
                )}
              </div>
            </div>

            {/* ── Caméra / Micro ───────────────────────────────────────── */}
            {(availableCameras.length > 1 || filteredMics.length > 1) && (
              <div style={{ display: 'flex', gap: 16 }}>
                {availableCameras.length > 1 && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                        <Camera size={20} color="#593BD9" />
                      </div>
                      <select value={selectedCamera} onChange={e => handleCameraChange(e.target.value)}
                        style={{ flex: 1, minWidth: 0, padding: 10, border: '2px solid #ddd', borderRadius: 8, fontSize: 14, background: 'white', color: '#111' }}>
                        {availableCameras.map(c => <option key={c.deviceId} value={c.deviceId}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {filteredMics.length > 1 && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, border: '2px solid #ddd', borderRadius: 8, background: 'white', flexShrink: 0 }}>
                        <Mic size={20} color="#593BD9" />
                      </div>
                      <select value={selectedMic} onChange={e => handleMicChange(e.target.value)}
                        style={{ flex: 1, minWidth: 0, padding: 10, border: '2px solid #ddd', borderRadius: 8, fontSize: 14, background: 'white', color: '#111' }}>
                        {filteredMics.map(m => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Mon flux ─────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gridTemplateRows: 'auto auto', columnGap: POTENTIOMETER_COL_GAP, rowGap: 6 }}>
              <h3 style={{ gridColumn: 1, gridRow: 1, fontSize: 14, fontWeight: 600, color: '#ffffff', margin: 0, textAlign: 'center' }}>Mon flux</h3>

              <div style={{ gridColumn: 1, gridRow: 2, minWidth: 0 }}>
                <div
                  ref={monFluxContainerRef}
                  onClick={() => toggleFullscreen('monFlux')}
                  style={{
                    background: '#000', borderRadius: isMonFluxFullscreen ? 0 : 12, overflow: 'hidden', cursor: 'pointer',
                    position: isMonFluxFullscreen ? 'fixed' : 'relative',
                    inset: isMonFluxFullscreen ? 0 : undefined,
                    zIndex: isMonFluxFullscreen ? 9999 : undefined,
                    aspectRatio: isMonFluxFullscreen ? undefined : '16 / 9',
                    width: '100%', height: isMonFluxFullscreen ? '100dvh' : undefined,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <video
                    ref={videoRef} autoPlay playsInline muted
                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                  />

                  {isOnAir && (
                    <div style={{
                      position: 'absolute', top: 12, right: 12,
                      background: 'rgba(220,38,38,0.92)',
                      boxShadow: '0 0 14px 4px rgba(239,68,68,0.75)',
                      color: 'white', padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 'bold',
                      display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none',
                      transition: 'background 0.3s ease, box-shadow 0.3s ease',
                    }}>
                      <span style={{ width: 8, height: 8, background: 'white', borderRadius: '50%', animation: 'pulse 2s ease-in-out infinite' }} />
                      On air
                    </div>
                  )}
                  {!connected && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: 'white', textAlign: 'center', pointerEvents: 'none' }}>
                      <div style={{ fontSize: 48, marginBottom: 10 }}>⏳</div>
                      <div style={{ fontSize: 14 }}>En attente...</div>
                    </div>
                  )}
                  {isMonFluxFullscreen && (
                    <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.65)', fontSize: 12, padding: '6px 14px', borderRadius: 20, pointerEvents: 'none', userSelect: 'none' }}>
                      Toucher pour quitter le plein écran
                    </div>
                  )}
                </div>
              </div>

              <div style={{ gridColumn: 2, gridRow: 2, display: 'flex' }}>
                {renderVolumePotentiometer(
                  previewVolume,
                  micLevel,
                  handlePreviewVolumeChange,
                  previewMuted,
                  handleTogglePreviewMute,
                  previewMuted ? 'Réactiver' : 'Couper le micro',
                  previewMuted ? <MicOff color="red" /> : <Mic color="green" />,
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {showInfoPanel && (
        <RemoteDevice_InfoPanel
          deviceInfo={getPlatformInfo()}
          streamDetails={activeStreamDetails}
          geolocation={geolocation}
          orientation={orientation}
          codecInfo={codecInfo}
          networkQuality={networkQuality}
          onClose={() => setShowInfoPanel(false)}
          onRequestGeo={requestGeolocation}
        />
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        /* Halo de l'étiquette d'état : anneau qui s'écarte puis s'efface.
           La couleur vient de la variable --halo posée en style inline. */
        @keyframes badgeHalo {
          0%   { box-shadow: 0 0 0 0 var(--halo); }
          70%  { box-shadow: 0 0 0 9px rgba(0,0,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
        }
        :fullscreen video, :-webkit-full-screen video { object-fit: contain; width: 100%; height: 100%; }
        :fullscreen, :-webkit-full-screen { background: #000; }
      `}</style>
    </div>
  );
};

export default RemoteWebRTCDeviceV3;